import React from 'react';

import type { SuggestedEditKind } from '@aline/common';
import { makeStyles } from 'tss-react/mui';

import { SuggestionActions } from './SuggestionActions';
import { SuggestionChip } from './SuggestionChip';

const useStyles = makeStyles<{ selectable: boolean }>()((theme, { selectable }) => ({
  // The ✕ button's own padding adds to this gap, so the visible space after the chip is 9px.
  decision: {
    alignItems: 'center',
    cursor: selectable ? 'pointer' : undefined,
    display: 'inline-flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
}));

interface SuggestionDecisionProps {
  /** Dims ✓ and explains why; the server refuses the same accept. */
  acceptDisabledReason: string | null;
  kind: SuggestedEditKind;
  onAccept: () => void;
  onReject: () => void;
  /** Why the AI proposed this, shown over the chip. */
  rationale: string;
  className?: string;
  /** Clicking the chip, e.g. to make the suggestion the current stop. */
  onSelect?: () => void;
}

/**
 * The kind chip with its ✕ / ✓ beside it, as every pending add, change or
 * delete shows it: on a rule row, a category header, a position card and the
 * playbook title. One place owns the spacing so they all match. A move is
 * not one of these; it has its own full-width strip.
 */
export const SuggestionDecision = React.memo(function SuggestionDecision({
  acceptDisabledReason,
  className,
  kind,
  onAccept,
  onReject,
  onSelect,
  rationale,
}: SuggestionDecisionProps): JSX.Element {
  const { classes, cx } = useStyles({ selectable: Boolean(onSelect) });

  return (
    <span className={cx(classes.decision, className)} onClick={onSelect}>
      <SuggestionChip kind={kind} rationales={[rationale]} />
      <SuggestionActions
        acceptDisabledReason={acceptDisabledReason}
        size={14}
        onAccept={onAccept}
        onReject={onReject}
      />
    </span>
  );
});
